import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';

import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { PlatformLocation } from '@angular/common';
import { from, Observable } from 'rxjs';
import { MaterialDateModule } from './shared/material-date.module';
import { NgxEditorModule } from 'ngx-editor';
import {
  provideMatomo,
  withRouter,
  withRouterInterceptors,
} from 'ngx-matomo-client';
import { MatomoAuthInterceptor } from './interceptors/matomo-auth.interceptor';
import { AuthErrorInterceptor } from './interceptors/auth-error.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { userReducer } from './store/user/user.reducer';
import { UserEffects } from './store/user/user.effects';
import { trendingCategoriesReducer } from './store/trending-categories/trending-categories.reducer';
import { TrendingCategoriesEffects } from './store/trending-categories/trending-categories.effects';
import { advisoryBoardOffersReducer } from './store/advisory-board-offers/advisory-board-offers.reducer';
import { AdvisoryBoardOffersEffects } from './store/advisory-board-offers/advisory-board-offers.effects';

// Function to check if we're on localhost
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

export const appConfig: ApplicationConfig = {
  providers: [
    provideMatomo(
      {
        siteId: 1,
        trackerUrl: 'https://sondis.matomo.cloud',
        disabled: isLocalhost, // Disable tracking on localhost
      },
      withRouter(),
      withRouterInterceptors([MatomoAuthInterceptor]),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(),
    provideAnimationsAsync(),
    importProvidersFrom(KeycloakAngularModule, MaterialDateModule),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    importProvidersFrom(
      NgxEditorModule.forRoot({
        locals: {
          // menu
          bold: 'Fett',
          italic: 'Kursiv',
          code: 'Code',
          underline: 'Unterstrichen',
          strike: 'Durchgestrichen',
          blockquote: 'Blockzitat',
          bullet_list: 'Aufzählungsliste',
          ordered_list: 'Nummerierte Liste',
          heading: 'Überschrift',
          h1: 'Überschrift 1',
          h2: 'Überschrift 2',
          h3: 'Überschrift 3',
          h4: 'Überschrift 4',
          h5: 'Überschrift 5',
          h6: 'Überschrift 6',
          align_left: 'Linksbündig',
          align_center: 'Zentriert',
          align_right: 'Rechtsbündig',
          align_justify: 'Blocksatz',
          text_color: 'Textfarbe',
          background_color: 'Hintergrundfarbe',
          horizontal_rule: 'Horizontale Linie',
          format_clear: 'Formatierung löschen',
          insertLink: 'Link einfügen',
          removeLink: 'Link entfernen',
          insertImage: 'Bild einfügen',
          indent: 'Einzug vergrößern',
          outdent: 'Einzug verkleinern',
          superscript: 'Hochgestellt',
          subscript: 'Tiefgestellt',
          undo: 'Rückgängig',
          redo: 'Wiederholen',
          // popups, forms, others...
          url: 'URL',
          text: 'Text',
          openInNewTab: 'In neuem Tab öffnen',
          insert: 'Einfügen',
          altText: 'Alternativtext',
          title: 'Titel',
          remove: 'Entfernen',
          enterValidUrl: 'Bitte eine gültige URL eingeben',
        },
      }),
    ),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(KeycloakService),
        inject(PlatformLocation),
      );
      return initializerFn();
    }),
    provideStore({
      user: userReducer,
      trendingCategories: trendingCategoriesReducer,
      advisoryBoardOffers: advisoryBoardOffersReducer,
    }),
    provideEffects([
      UserEffects,
      TrendingCategoriesEffects,
      AdvisoryBoardOffersEffects,
    ]),
  ],
};

function initializeApp(keycloak: KeycloakService, location: PlatformLocation) {
  return (): Observable<unknown> => {
    return from(
      keycloak.init({
        config: {
          url: 'https://auth.sondis.at',
          realm: 'Sondis',
          clientId: 'external-client',
        },
        initOptions: {
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}${getBaseHref(
            location,
          )}/assets/silent-check-sso.html`,
        },
      }),
    );
  };
}

export const getBaseHref = (location: PlatformLocation): string => {
  let baseHref = location.getBaseHrefFromDOM();

  if (baseHref === '/') {
    baseHref = '';
  }

  return baseHref;
};
